import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Lock,
  SignOut,
  DownloadSimple,
  FileText,
  Megaphone,
  Newspaper,
  Sparkle,
  Package,
  WhatsappLogo,
  Envelope,
  CircleNotch,
} from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { contacts } from "@/data/content";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const STORAGE_KEY = "homezen.affiliates.auth";
const SESSION_DAYS = 30;
const PASSWORD = process.env.REACT_APP_AFFILIATES_PASSWORD;

const isSessionValid = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const { exp } = JSON.parse(raw);
    return typeof exp === "number" && exp > Date.now();
  } catch (_) {
    return false;
  }
};

const persistSession = () => {
  const exp = Date.now() + SESSION_DAYS * 24 * 60 * 60 * 1000;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ exp }));
};

const clearSession = () => {
  window.localStorage.removeItem(STORAGE_KEY);
};

/* ---------- Gate ---------- */

const Gate = ({ onSuccess }) => {
  const { t } = useLang();
  const g = t.affiliates.gate;
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Small delay to prevent brute force feel
    await new Promise((r) => setTimeout(r, 400));
    if (pwd === PASSWORD) {
      persistSession();
      onSuccess();
    } else {
      setErr(true);
    }
    setLoading(false);
  };

  const waHref = `https://wa.me/${contacts.whatsappNumber}?text=${encodeURIComponent(
    "Ciao Home Zen, ho bisogno della password Area Affiliati."
  )}`;

  return (
    <div className="min-h-screen bg-[#111111] text-[#FAFAF7] grain flex items-center justify-center px-6 py-24">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
        data-testid="affiliates-gate"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs tracking-luxe uppercase text-white/50 hover:text-white transition-colors mb-8"
          data-testid="gate-back-home"
        >
          <ArrowLeft size={12} /> Home
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 border border-white/15 flex items-center justify-center">
            <Lock size={16} weight="light" />
          </div>
          <span className="text-[10px] tracking-luxe uppercase text-white/50">{g.kicker}</span>
        </div>

        <h1 className="font-display text-4xl md:text-5xl leading-[1.05] mb-4">{g.title}</h1>
        <p className="text-white/60 leading-relaxed mb-10">{g.subtitle}</p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="relative">
            <input
              type="password"
              value={pwd}
              onChange={(e) => {
                setPwd(e.target.value);
                setErr(false);
              }}
              placeholder={g.placeholder}
              data-testid="gate-password"
              autoComplete="current-password"
              autoFocus
              className={`w-full bg-transparent border-b py-3 text-lg text-white placeholder-white/30 focus:outline-none transition-colors ${
                err ? "border-red-400" : "border-white/20 focus:border-[#8B7355]"
              }`}
            />
          </div>

          <AnimatePresence>
            {err && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                data-testid="gate-error"
                className="text-sm text-red-400"
              >
                {g.error}
              </motion.p>
            )}
          </AnimatePresence>

          <button
            type="submit"
            disabled={loading || !pwd}
            data-testid="gate-submit"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-white text-[#111111] text-sm px-6 py-3.5 hover:bg-[#8B7355] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <CircleNotch size={16} className="animate-spin" weight="bold" /> : null}
            {g.submit}
            <ArrowRight size={14} weight="light" />
          </button>
        </form>

        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-10 inline-flex items-center gap-2 text-xs tracking-luxe uppercase text-white/50 hover:text-[#25D366] transition-colors"
        >
          <WhatsappLogo size={14} weight="fill" />
          {g.contactHelp}
        </a>
      </motion.div>
    </div>
  );
};

/* ---------- Private area content ---------- */

const SectionHeader = ({ icon: Icon, kicker, title, subtitle }) => (
  <div className="mb-10">
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} weight="light" className="text-[#8B7355]" />
      <span className="text-[10px] tracking-luxe uppercase text-[#8B7355]">{kicker}</span>
    </div>
    <h2 className="font-display text-3xl md:text-4xl leading-tight mb-2">{title}</h2>
    <p className="text-[#1A1A1A]/60">{subtitle}</p>
  </div>
);

const AffiliatesContent = ({ onLogout }) => {
  const { t, lang } = useLang();
  const a = t.affiliates;

  const waPurchasing = `https://wa.me/${contacts.whatsappNumber}?text=${encodeURIComponent(
    "Centrale acquisti Home Zen — ordine affiliato."
  )}`;

  return (
    <div className="App bg-[#FAFAF7] text-[#1A1A1A] min-h-screen">
      <Navbar />

      <main className="pt-32 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mb-20 flex flex-wrap items-end justify-between gap-6"
          >
            <div>
              <div className="flex items-center gap-2 mb-4 text-[#8B7355]">
                <Lock size={14} weight="light" />
                <span className="text-[10px] tracking-luxe uppercase">{a.kicker}</span>
              </div>
              <h1 className="font-display text-5xl md:text-6xl leading-[0.98] mb-4">{a.title}</h1>
              <p className="text-lg text-[#1A1A1A]/70 max-w-2xl">{a.subtitle}</p>
            </div>
            <button
              type="button"
              onClick={onLogout}
              data-testid="affiliates-logout"
              className="inline-flex items-center gap-2 border border-black/10 text-sm px-5 py-2.5 hover:bg-[#1A1A1A] hover:text-white hover:border-[#1A1A1A] transition-colors"
            >
              <SignOut size={14} weight="light" />
              {a.logout}
            </button>
          </motion.header>

          {/* Downloads */}
          <section id="downloads" className="mb-24" data-testid="section-downloads">
            <SectionHeader
              icon={FileText}
              kicker={a.sections.downloads}
              title={a.sections.downloads}
              subtitle={a.downloads.subtitle}
            />
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {a.downloads.items.map((it) => (
                <div
                  key={it.key}
                  data-testid={`download-${it.key}`}
                  className={`p-6 border ${
                    it.available ? "border-black/10 bg-white" : "border-dashed border-black/10 bg-transparent"
                  }`}
                >
                  <FileText size={18} weight="light" className="text-[#8B7355] mb-4" />
                  <p className="font-display text-xl mb-2">{it.title}</p>
                  <p className="text-sm text-[#1A1A1A]/60 leading-relaxed mb-5 min-h-[3rem]">{it.desc}</p>
                  {it.available ? (
                    <a
                      href={it.href}
                      download
                      className="inline-flex items-center gap-2 text-sm text-[#1A1A1A] hover:text-[#8B7355] transition-colors"
                    >
                      <DownloadSimple size={14} weight="light" />
                      {lang === "zh" ? "下载 PDF" : lang === "en" ? "Download PDF" : "Scarica PDF"}
                    </a>
                  ) : (
                    <span className="inline-flex text-[10px] tracking-luxe uppercase text-[#1A1A1A]/40 border border-black/10 px-2 py-1">
                      {lang === "zh" ? "即将推出" : lang === "en" ? "Coming soon" : "In arrivo"}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Campaigns */}
          <section id="campaigns" className="mb-24" data-testid="section-campaigns">
            <SectionHeader
              icon={Megaphone}
              kicker={a.sections.campaigns}
              title={a.sections.campaigns}
              subtitle={a.campaigns.subtitle}
            />
            <div className="grid md:grid-cols-3 gap-4">
              {a.campaigns.items.map((c, i) => (
                <div key={i} className="p-6 bg-[#F3EFEA] flex flex-col justify-between min-h-[220px]">
                  <span className="inline-flex self-start text-[10px] tracking-luxe uppercase text-[#8B7355] border border-[#8B7355]/40 px-2 py-1 mb-4">
                    {c.tag}
                  </span>
                  <div>
                    <p className="font-display text-xl mb-2 leading-tight">{c.title}</p>
                    <p className="text-sm text-[#1A1A1A]/70 leading-relaxed">{c.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* News */}
          <section id="news" className="mb-24" data-testid="section-news">
            <SectionHeader
              icon={Newspaper}
              kicker={a.sections.news}
              title={a.sections.news}
              subtitle={a.news.subtitle}
            />
            <ul className="divide-y divide-black/10 border-y border-black/10">
              {a.news.items.map((n, i) => (
                <li key={i} className="py-6 grid md:grid-cols-[100px_1fr] gap-4 md:gap-8">
                  <span className="text-xs font-mono tracking-wide text-[#8B7355]">{n.date}</span>
                  <div>
                    <p className="font-display text-xl mb-1">{n.title}</p>
                    <p className="text-sm text-[#1A1A1A]/70 leading-relaxed">{n.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Social Kit link */}
          <section id="kit" className="mb-24" data-testid="section-kit">
            <div className="p-8 md:p-12 bg-[#1A1A1A] text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 mb-3 text-[#8B7355]">
                  <Sparkle size={14} weight="fill" />
                  <span className="text-[10px] tracking-luxe uppercase">{a.sections.kit}</span>
                </div>
                <p className="font-display text-3xl md:text-4xl mb-3 leading-tight">
                  {lang === "zh"
                    ? "已为您准备好一整月社媒内容。"
                    : lang === "en"
                    ? "A full month of social content, ready for you."
                    : "Un mese di contenuti social, già pronti."}
                </p>
                <p className="text-white/60 max-w-xl">{a.kit.subtitle}</p>
              </div>
              <Link
                to="/social-kit"
                data-testid="affiliates-kit-link"
                className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] text-sm px-6 py-3.5 hover:bg-[#8B7355] hover:text-white transition-colors shrink-0"
              >
                {a.kit.cta}
                <ArrowRight size={14} weight="light" />
              </Link>
            </div>
          </section>

          {/* Central purchasing */}
          <section id="purchasing" data-testid="section-purchasing">
            <SectionHeader
              icon={Package}
              kicker={a.sections.purchasing}
              title={a.sections.purchasing}
              subtitle={a.purchasing.subtitle}
            />
            <div className="grid md:grid-cols-2 gap-4">
              <a
                href={`mailto:${contacts.email}?subject=${encodeURIComponent("Ordine affiliato Home Zen")}`}
                className="p-6 border border-black/10 bg-white flex items-center gap-4 hover:border-[#8B7355] transition-colors group"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-[#F3EFEA] group-hover:bg-[#8B7355] group-hover:text-white transition-colors">
                  <Envelope size={18} weight="light" />
                </div>
                <div>
                  <p className="text-[10px] tracking-luxe uppercase text-[#1A1A1A]/50 mb-1">
                    {a.purchasing.emailLabel}
                  </p>
                  <p className="font-medium text-[#1A1A1A]">{contacts.email}</p>
                </div>
              </a>
              <a
                href={waPurchasing}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="affiliates-wa-purchasing"
                className="p-6 border border-black/10 bg-white flex items-center gap-4 hover:border-[#25D366] transition-colors group"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-[#F3EFEA] group-hover:bg-[#25D366] group-hover:text-white transition-colors">
                  <WhatsappLogo size={18} weight="fill" />
                </div>
                <div>
                  <p className="text-[10px] tracking-luxe uppercase text-[#1A1A1A]/50 mb-1">
                    {a.purchasing.whatsappLabel}
                  </p>
                  <p className="font-medium text-[#1A1A1A]">{contacts.phoneDisplay}</p>
                </div>
              </a>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

/* ---------- Page ---------- */

export default function AffiliatesArea() {
  const [authed, setAuthed] = useState(() => isSessionValid());
  const navigate = useNavigate();

  useEffect(() => {
    if (!PASSWORD) {
      // Misconfig: no password set — bounce to home
      navigate("/", { replace: true });
    }
  }, [navigate]);

  const onLogout = () => {
    clearSession();
    setAuthed(false);
  };

  return authed ? (
    <AffiliatesContent onLogout={onLogout} />
  ) : (
    <Gate onSuccess={() => setAuthed(true)} />
  );
}
